import { blinkCaret } from './blink-caret.module.css';

import ButtonBase from '@mui/material/ButtonBase';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

interface ReasoningPartProps {
	text: string;
	/** `'running'` while the model is still generating this block. */
	status: string;
}

/**
 * Collapsible reasoning block shown between tool calls.
 * Open by default, including after completion, unless toggled by the user.
 *
 * @param props.text - The reasoning text content.
 * @param props.status - `'running'` while streaming, anything else once complete.
 */
export function ReasoningPart({ text, status }: ReasoningPartProps) {
	const isRunning = status === 'running';

	// null means "historical block" — duration is only shown for live-streamed reasoning.
	const startTimeRef = useRef<number | null>(isRunning ? Date.now() : null);
	const durationCaptured = useRef(false);
	const [durationSec, setDurationSec] = useState<number | null>(null);

	useEffect(() => {
		if (isRunning) {
			startTimeRef.current ??= Date.now();
		} else if (!durationCaptured.current && startTimeRef.current !== null) {
			durationCaptured.current = true;
			setDurationSec(Math.round((Date.now() - startTimeRef.current) / 1000));
		}
	}, [isRunning]);

	// null means "use default"; non-null is the user's explicit choice.
	const [userPref, setUserPref] = useState<boolean | null>(null);
	// Default: open while running and after completion; non-null userPref wins.
	const expanded = userPref ?? true;

	const toggle = () => {
		setUserPref(!expanded);
	};

	const meta =
		durationSec !== null ?
			durationSec > 0 ?
				`reasoned for ${durationSec}s`
			:	'reasoned for <1s'
		:	null;

	const { label, body } = getReasoningDisplayText(text);

	return (
		<div className='mb-2.5'>
			<ButtonBase onClick={toggle} className='flex w-full items-center gap-1.5 text-left'>
				<span className='shrink-0 font-mono text-sm text-gray-a8 select-none'>{expanded ? '▾' : '▸'}</span>
				<span className='flex-1 font-mono text-sm tracking-[0.08em] text-gray-a8 uppercase'>{label}</span>
				{meta !== null && <span className='text-sm tracking-normal text-gray-a7 normal-case'>{meta}</span>}
			</ButtonBase>
			{expanded && (
				<p className='my-1.5 border-l-2 border-gray-a4 py-1 pl-2 text-sm leading-[1.65] whitespace-pre-wrap text-gray-a9 italic'>
					{body}
					{isRunning && <span className={clsx('ml-0.5 inline-block h-2.5 w-1 bg-gray-a7 align-middle', blinkCaret)} />}
				</p>
			)}
		</div>
	);
}

function getReasoningDisplayText(text: string): { label: string; body: string } {
	const firstLine = /^[^\n]+/.exec(text)?.[0] ?? '';
	const bodyAfterFirstLine = text.slice(firstLine.length).replace(/^\n+/, '');
	const firstLineLabel = firstLine.replace(/\*\*/g, '').trim();
	const hasBodyAfterFirstLine = bodyAfterFirstLine.trim().length > 0;
	const firstLineLooksLikeHeading =
		firstLineLabel.length > 0 && firstLineLabel.length <= 80 && !/[.!?]$/.test(firstLineLabel);

	if (hasBodyAfterFirstLine && firstLineLooksLikeHeading) {
		return { label: firstLineLabel, body: bodyAfterFirstLine };
	}

	return { label: 'thinking', body: text };
}
